import {
  Activity,
  BarChart3,
  Cpu,
  Database,
  Fingerprint,
  Globe,
  KeyRound,
  Laptop,
  Layers,
  Lock,
  type LucideIcon,
  MessageSquare,
  Network,
  Presentation,
  Radar,
  Route,
  ScanSearch,
  Search,
  Shield,
  ShieldCheck,
  Siren,
  Sparkles,
  Users,
} from 'lucide-react';
import { GtmContentListItemKind } from '../../../types/gtm-architecture';

/**
 * Per-slug presentation overlay for the generic architecture page.
 *
 * Everything a page *says* comes from the `go_to_market` content tables. What lives here is
 * chrome the database has no column for: the architecture diagram image, accent colors, which
 * Lucide icon represents a named component, and the CTA buttons.
 * A slug with no entry renders with the defaults below — the page never depends on one
 * existing.
 *
 * Note: there is deliberately no per-slug browser title. The tab always reads "Security Hub"
 * (set once in `public/index.html`); no page may override `document.title`.
 */

export interface ArchitectureCtaButton {
  label: string;
  icon?: LucideIcon;
  url?: string;
}

export interface ArchitectureOverride {
  /** Icon in the hero badge chip. */
  badgeIcon?: LucideIcon;
  /** Static architecture image. Slugs without one fall back to component cards. */
  diagram?: { src: string; alt: string };
  /** Accent for the second (gradient) hero line. */
  heroGradient?: string;
  /** Component name -> icon, for the architecture cards. */
  componentIcons?: Record<string, LucideIcon>;
  /** Component name -> accent color, for the architecture cards. */
  componentAccents?: Record<string, string>;
  /** Overrides the heading above a group of `list_items` of one kind. */
  listLabels?: Partial<Record<GtmContentListItemKind, string>>;
  /**
   * Tag values that become the use-case filter row, in display order. Use-case categories are
   * stored as the first tag on each use case, so the tabs are just the tags worth filtering on.
   */
  useCaseTabs?: string[];
  ctaButtons?: ArchitectureCtaButton[];
}

export const DEFAULT_LIST_LABELS: Record<GtmContentListItemKind, string> = {
  question: 'QUALIFYING QUESTIONS',
  listen_for: 'WHAT TO LISTEN FOR',
  challenge: 'COMMON CHALLENGES TO ADDRESS',
  objection: 'OBJECTIONS & RESPONSES',
  internal_resource: 'ADDITIONAL SALES ENABLEMENT RESOURCES',
};

export const DEFAULT_HERO_GRADIENT =
  'linear-gradient(90deg, #3070e7, #16bdeb 30%, #ff007f 75%, #ff9000)';

export const DEFAULT_CTA_BUTTONS: ArchitectureCtaButton[] = [
  { label: 'Start Sales Conversation', icon: MessageSquare },
  { label: 'Schedule Demo', icon: Presentation },
];

/** Accents cycled through for component cards whose name isn't in `componentAccents`. */
export const COMPONENT_ACCENT_CYCLE = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b'];

const ARCHITECTURE_OVERRIDES: Record<string, ArchitectureOverride> = {
  'hybrid-mesh-firewall': {
    // TEMPORARY: architecture diagram image not yet created — commenting out so the page
    // falls back to component cards instead of rendering a broken image. Restore when
    // /images/hmf-architecture.png exists.
    // diagram: {
    //   src: '/images/hmf-architecture.png',
    //   alt: 'Cisco Hybrid Mesh Firewall Architecture',
    // },
    componentIcons: {
      'Mesh Controller': Network,
      'Threat Intelligence Engine': Radar,
      'Adaptive Security Nodes': Shield,
      'Global Policy Engine': Globe,
    },
  },

  'universal-ztna': {
    // TEMPORARY: architecture diagram image not yet created — commenting out so the page
    // falls back to component cards instead of rendering a broken image. Restore when
    // /images/zta-architecture.png exists.
    // diagram: {
    //   src: '/images/zta-architecture.png',
    //   alt: 'Cisco Zero Trust Access Architecture',
    // },
    componentIcons: {
      'Policy & Access Orchestration': Shield,
      'Identity Provider & MFA': Fingerprint,
      'Device Posture & Trust Signals': Laptop,
      'Secure Web / Internet Gateway': Globe,
      'Private App Access Connector (ZTNA)': Layers,
      'User/Entity Risk Analytics (UEBA)': BarChart3,
      'Network Enforcement Points (Campus/Branch)': Network,
      'Digital Experience & Path Visibility': Route,
    },
  },

  'security-cloud-control': {
    badgeIcon: ShieldCheck,
    componentIcons: {
      'Cloud-Delivered Management Plane': Globe,
      'Unified Policy & Object Management': Layers,
      'AI Assistant for Security': Sparkles,
      'Change Management & Insights': Activity,
      'Multicloud & Hybrid Firewall Control': Network,
    },
    useCaseTabs: ['migration', 'multicloud', 'operations', 'compliance'],
  },

  'ai-security': {
    badgeIcon: Sparkles,
    componentIcons: {
      'Model Discovery': ScanSearch,
      'Runtime Protection': ShieldCheck,
      'AI Guardrails': Lock,
      'Threat Intelligence': Radar,
      'AI Inventory': Cpu,
    },
  },

  identity: {
    badgeIcon: Fingerprint,
    // Cisco's Identity enablement frames these as the events that start a deal rather than as
    // things a rep listens for in discovery.
    listLabels: { listen_for: 'BUYING TRIGGERS' },
    componentIcons: {
      'Identity Provider': KeyRound,
      'Multi-Factor Authentication': Fingerprint,
      'Device Trust': Laptop,
      'Identity Intelligence': BarChart3,
      'Single Sign-On': Users,
    },
  },

  splunk: {
    badgeIcon: Database,
    componentIcons: {
      'Data Ingestion Layer': Database,
      'Search & Correlation Engine': Search,
      'Observability Pipeline': Activity,
      'Threat Detection Content': Radar,
    },
  },

  'identity-segmentation': {
    diagram: {
      src: '/assets/diagrams/IBS.png',
      alt: 'Identity Based Segmentation Architecture',
    },
    componentIcons: {
      'Identity Provider': KeyRound,
      'Policy Engine': Shield,
      'Network Enforcement': Network,
      'Analytics Engine': BarChart3,
    },
    componentAccents: {
      'Identity Provider': '#6366f1',
      'Policy Engine': '#ec4899',
      'Network Enforcement': '#14b8a6',
      'Analytics Engine': '#f59e0b',
    },
    useCaseTabs: ['compliance', 'industrial', 'enterprise'],
  },

  'power-security-ops': {
    diagram: {
      src: '/assets/diagrams/PSO.png',
      alt: 'Power Security Operations Architecture',
    },
    componentIcons: {
      'Security Orchestration': Layers,
      'Threat Detection Engine': Radar,
      'Response Automation': Siren,
      'Intelligence Platform': Lock,
    },
    componentAccents: {
      'Security Orchestration': '#6366f1',
      'Threat Detection Engine': '#ec4899',
      'Response Automation': '#14b8a6',
      'Intelligence Platform': '#f59e0b',
    },
    useCaseTabs: ['detection', 'automation', 'compliance'],
  },
};

export const architectureOverride = (slug: string): ArchitectureOverride =>
  ARCHITECTURE_OVERRIDES[slug] || {};

export default ARCHITECTURE_OVERRIDES;
